import {
  useLayoutEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import { useLocation } from 'react-router-dom'

import { Footer } from '@/components/layout/Footer'

import { Header } from '@/components/layout/Header'

import { GuestBanner } from '@/components/layout/GuestBanner'
import { PageEnter } from '@/components/layout/PageEnter'

import { useAppSelector } from '@/hooks/redux'

type AppLayoutProps = PropsWithChildren<{
  /** Full-width main (no max-width shell) — use for dashboard and similar full-bleed pages */

  fullWidth?: boolean

  /** Drop main top padding so the hero can sit closer under the header (e.g. home) */

  flushTop?: boolean

  /** Edge-to-edge main: no padding, no max-width. For pages that own their
   *  full-bleed sections (gradients, borders that reach the viewport edge). */

  bleed?: boolean

  /**
   * In-page stagger only. `sections` = dashboard header → metrics → tabs.
   * Route crossfade is handled globally by the router outlet.
   */
  pageEnter?: 'sections' | 'none'
}>

export const AppLayout = ({
  children,
  fullWidth,
  flushTop,
  bleed,
  pageEnter: pageEnterMode = 'none',
}: AppLayoutProps) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const { pathname } = useLocation()

  // Guest nudge on every page a signed-out visitor can land on EXCEPT the
  // homepage. Protected routes redirect guests to /login before AppLayout
  // ever renders, and the auth pages don't use AppLayout — so in practice
  // this surfaces on /scoring today, plus any page later opened to guests.
  const showGuestBanner =
    !isAuthenticated && pathname !== '/' && pathname !== '/about'

  const headerStackRef = useRef<HTMLDivElement>(null)
  const [headerOffset, setHeaderOffset] = useState(56)

  useLayoutEffect(() => {
    const el = headerStackRef.current
    if (!el) return
    const sync = () => setHeaderOffset(el.offsetHeight)
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => ro.disconnect()
  }, [showGuestBanner])

  return (
    <div className="flex min-h-screen w-full min-w-0 flex-col overflow-x-hidden bg-[var(--background)] text-[var(--foreground)]">
      <div
        ref={headerStackRef}
        className="fixed top-0 right-0 left-0 z-50 w-full"
      >
        <Header />
        {showGuestBanner ? <GuestBanner /> : null}
      </div>
      <div
        aria-hidden
        className="pointer-events-none shrink-0"
        style={{ height: headerOffset }}
      />

      <main
        className={
          bleed
            ? 'w-full min-w-0 flex-1 overflow-x-hidden'
            : fullWidth
              ? flushTop
                ? 'w-full min-w-0 flex-1 overflow-x-hidden px-4 pt-0 pb-6 sm:px-6 sm:pt-0 sm:pb-8 lg:px-10 lg:pb-10'
                : 'w-full min-w-0 flex-1 overflow-x-hidden px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10'
              : flushTop
                ? 'mx-auto w-full max-w-7xl min-w-0 flex-1 overflow-x-hidden px-4 pt-0 pb-8 sm:px-6 sm:pt-0 sm:pb-10'
                : 'mx-auto w-full max-w-7xl min-w-0 flex-1 overflow-x-hidden px-4 py-8 sm:px-6 sm:py-10'
        }
      >
        {pageEnterMode === 'sections' ? (
          <PageEnter staggerSections>{children}</PageEnter>
        ) : (
          children
        )}
      </main>

      <Footer />
    </div>
  )
}
